<script lang="ts">
import type { MediaFile } from '@monogrid/vue-boost/cms'
import { computed, defineComponent, PropType, ref } from 'vue'

import CtaIcon from '@/components/atoms/CtaIcon.vue'
import CtaPrimary from '@/components/atoms/CtaPrimary.vue'
import LayerImage from '@/components/atoms/LayerImage.vue'
import LayerVideo from '@/components/atoms/LayerVideo.vue'
import ModalWrapper from '@/components/atoms/ModalWrapper.vue'
import VideoPlayer from '@/components/atoms/VideoPlayer.vue'
import { useBreakpoint } from '@/functions/useBreakpoint'

export default defineComponent({
  name: 'VideoLauncher',
  components: {
    CtaIcon,
    CtaPrimary,
    ModalWrapper,
    VideoPlayer,
    LayerImage,
    LayerVideo
  },
  props: {
    opacity: { type: Number, default: 1 },
    order: { type: Number, default: 0 },
    previewMedia: { type: Object as PropType<MediaFile>, required: true },
    video: { type: Object as PropType<MediaFile>, required: true },
    videoMobile: { type: Object as PropType<MediaFile>, default: null },
    webgl: { type: Boolean, default: true }
  },
  setup (props, { attrs, slots, emit }) {
    const showModal = ref(false)
    const bp = useBreakpoint()
    const previewComponent = computed(() => props.previewMedia.mimeType.startsWith('video') ? LayerVideo : LayerImage)
    const videoData = computed(() => props.videoMobile && bp.portrait ? props.videoMobile : props.video)

    return {
      previewComponent,
      showModal,
      videoData
    }
  }
})
</script>

<template>
  <div class="wrapper flex items-center justify-center cursor-pointer">
    <component
      :is="previewComponent"
      class="!absolute inset-0"
      cover
      dithered
      :transparent="true"
      :opacity="opacity"
      :data="previewMedia"
      :order="order"
      :webgl="webgl"
      @click="showModal = true"
    />
    <CtaIcon
      class="relative" icon="play"
      @click="showModal = true"
    >
      <template #labelStart>SHOW</template>
      <template #labelEnd>REEL</template>
    </CtaIcon>

    <ModalWrapper
      v-slot="{ dismiss }"
      v-model:visible="showModal"
    >
      <div class="container-big w-full absolute top-0 z-2 pt-5 text-right pointer-events-none">
        <CtaPrimary class="close-button w-13 pointer-events-auto uppercase" green @click="dismiss">
          {{ $t('Close') }}
        </CtaPrimary>
      </div>
      <VideoPlayer v-reveal="'fadeIn'" v-reveal:options="{ delay: 0.3 }" class="w-full h-full bg-black" cover autoplay loop :data="videoData" />
    </ModalWrapper>
  </div>
</template>

<style lang="scss" scoped>
.wrapper {
  @include ensure-position;
}

.close-button {
  margin-right: var(--scrollbar-placeholder, 0);
}
</style>
